import React, { Fragment, ReactElement, memo } from 'react';

interface IProps {
  children: ReactElement;
}

class Container {
  settings: Map<string, ReactElement[]>;

  private static Instance: Container;

  private constructor() {
    this.settings = new Map();
  }

  public getElements(id: string): ReactElement[] {
    return this.settings.get(id) || [];
  }

  public register(element: ReactElement): void {
    if (!('node' in element.props) || !element.props.node?.id) {
      console.warn(
        'SettingsContainer: Invalid element - missing node or node.id'
      );
      return;
    }

    const elId = element.props.node.id;

    if (!this.settings.has(elId)) {
      this.settings.set(elId, []);
    }

    // Check if element already exists to prevent duplicates
    const existingElements = this.settings.get(elId)!;

    // Only add if not already registered
    const exists = existingElements.some(
      (existing) =>
        existing.key === element.key && existing.type === element.type
    );

    if (!exists) {
      existingElements.push(element);
    }
  }

  public unregister(id: string): void {
    this.settings.delete(id);
  }

  public clearAll(): void {
    this.settings.clear();
  }

  public static getContainer() {
    if (!Container.Instance) {
      Container.Instance = new Container();
    }
    return Container.Instance;
  }
}

export const SettingsContainer = ({ children }: IProps): null => {
  const Settings = Container.getContainer();
  Settings.register(children);
  return null;
};

SettingsContainer.Fill = memo(
  ({ id }: { id: string }): ReactElement => {
    const Settings = Container.getContainer();
    const elements = Settings.getElements(id);

    // Log for debugging - remove in production
    if (elements.length > 1) {
      console.warn(
        `SettingsContainer.Fill: Found ${elements.length} elements for id ${id}`
      );
    }

    return (
      <Fragment>
        {elements.map((element, index) =>
          React.cloneElement(element, { key: `${id}-${index}` })
        )}
      </Fragment>
    );
  }
);
